import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Building2, FileText, ClipboardCheck, ShieldCheck,
  Activity as ActivityIcon, Star, TrendingUp, AlertTriangle, X,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
// Interactive stat card — every dashboard tile passes through this.
// ─────────────────────────────────────────────────────────────
const TONES = {
  primary: { border: "border-sky-200 hover:border-sky-600", iconBg: "bg-sky-100 text-sky-700", accent: "text-sky-900" },
  emerald: { border: "border-emerald-200 hover:border-emerald-600", iconBg: "bg-emerald-100 text-emerald-700", accent: "text-emerald-900" },
  violet: { border: "border-violet-200 hover:border-violet-600", iconBg: "bg-violet-100 text-violet-700", accent: "text-violet-900" },
  amber: { border: "border-amber-200 hover:border-amber-600", iconBg: "bg-amber-100 text-amber-800", accent: "text-amber-900" },
  rose: { border: "border-rose-200 hover:border-rose-600", iconBg: "bg-rose-100 text-rose-700", accent: "text-rose-900" },
  slate: { border: "border-zinc-200 hover:border-zinc-900", iconBg: "bg-zinc-100 text-zinc-700", accent: "text-zinc-900" },
};

export function StatCard({ label, value, sub, testId, icon: Icon, tone = "slate", onClick, to }) {
  const t = TONES[tone] || TONES.slate;
  const wrapperCls =
    `group relative border ${t.border} bg-white p-4 rounded-sm cursor-pointer transition-all
     hover:shadow-md hover:-translate-y-0.5`;
  const body = (
    <>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{label}</div>
        {Icon && (
          <span className={`w-7 h-7 rounded-sm flex items-center justify-center ${t.iconBg}`}>
            <Icon className="w-3.5 h-3.5" strokeWidth={2} />
          </span>
        )}
      </div>
      <div className={`text-3xl font-semibold mt-2 tabular-nums ${t.accent}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </>
  );
  if (to) return <Link to={to} data-testid={testId} className={wrapperCls}>{body}</Link>;
  return <button onClick={onClick} data-testid={testId} className={`${wrapperCls} text-left w-full`}>{body}</button>;
}


// ─────────────────────────────────────────────────────────────
// Generic drawer modal — used when a tile drills into a filtered list.
// ─────────────────────────────────────────────────────────────
export function ListDrawer({ title, subtitle, onClose, children, testId }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex justify-end" onClick={onClose} data-testid={testId}>
      <div className="bg-white w-[95vw] max-w-2xl h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">{title}</div>
            {subtitle && <div className="text-[11px] text-zinc-500">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="drawer-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Global Dashboard page — plugs into Global.jsx tabs.
// ─────────────────────────────────────────────────────────────
export default function GlobalDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [drawer, setDrawer] = useState(null);
  const [buFilter, setBuFilter] = useState(""); // "" = all
  const [bus, setBus] = useState([]);

  const load = async (buId) => {
    try {
      const { data } = await api.get(`/global/dashboard${buId ? `?bu_id=${buId}` : ""}`);
      setData(data);
    } catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { load(buFilter); }, [buFilter]);
  useEffect(() => {
    api.get("/business-units").then((r) => setBus(r.data)).catch(() => {});
  }, []);

  if (error) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="global-dashboard-error">{error}</div>;
  if (!data) return <div className="text-sm text-zinc-500">Loading platform dashboard…</div>;

  const { counts } = data;
  const isFiltered = !!data.filter?.bu_id;
  const scopeLabel = isFiltered ? data.filter.bu_name : "All Business Units";
  const usersDenom = counts.users || 0;

  return (
    <div className="space-y-6" data-testid="global-dashboard">
      {/* BU filter row */}
      <div className="flex items-center gap-3 flex-wrap border-b border-zinc-200 pb-4">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Filter:</span>
        <button
          data-testid="gd-filter-all"
          onClick={() => setBuFilter("")}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${!buFilter ? "bg-sky-600 text-white border-sky-600" : "bg-white text-zinc-700 border-zinc-200 hover:border-sky-400"}`}
        >
          All Business Units
        </button>
        {bus.map((b) => (
          <button
            key={b.id}
            data-testid={`gd-filter-${b.id}`}
            onClick={() => setBuFilter(b.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${buFilter === b.id ? "bg-sky-600 text-white border-sky-600" : "bg-white text-zinc-700 border-zinc-200 hover:border-sky-400"}`}
          >
            {b.name}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-zinc-500">
          Showing: <b data-testid="gd-scope-label" className="text-zinc-800">{scopeLabel}</b>
        </span>
      </div>

      {/* Row 1 — headline counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Business Units" value={counts.business_units} icon={Building2} tone="primary"
          sub={isFiltered ? "Total platform" : "Click to open the BU grid"}
          testId="gd-stat-bus" to="/business-units" />
        <StatCard label="People" value={counts.users} icon={Users} tone="violet"
          sub={Object.entries(counts.users_by_role || {}).map(([k, v]) => `${v} ${k.replace("_", " ")}`).join(" · ")}
          testId="gd-stat-users" onClick={() => setDrawer({ key: "users", title: `People — ${scopeLabel}`, subtitle: `${counts.users} total` })} />
        <StatCard label="Position Profiles" value={counts.position_profiles} icon={ClipboardCheck} tone="emerald"
          testId="gd-stat-profiles" onClick={() => setDrawer({ key: "profiles", title: "Position profiles", subtitle: scopeLabel })} />
        <StatCard label="Templates" value={counts.templates} icon={FileText} tone="amber"
          sub={Object.entries(data.templates_by_status || {}).map(([k, v]) => `${v} ${k.replace("_", " ")}`).join(" · ")}
          testId="gd-stat-templates" onClick={() => setDrawer({ key: "templates", title: `Templates — ${scopeLabel}`, subtitle: `${counts.templates} templates` })} />
      </div>

      {/* Row 2 — reviews / policies / signoff / skills */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Reviews" value={`${counts.reviews_completed}/${counts.reviews_total}`} tone="emerald"
          icon={TrendingUp}
          sub={`${counts.reviews_total ? Math.round(100 * counts.reviews_completed / counts.reviews_total) : 0}% completion overall`}
          testId="gd-stat-reviews" to={buFilter ? `/reviews?bu=${buFilter}` : "/reviews"} />
        <StatCard label="Policies" value={data.policies.total} icon={FileText} tone="primary"
          sub={`${data.policies.approved} approved · ${data.policies.pending_approval} pending`}
          testId="gd-stat-policies"
          onClick={() => setDrawer({ key: "policies", title: `Policies — ${scopeLabel}`, subtitle: `${data.policies.total} total` })} />
        <StatCard label="SOPs" value={data.sops.total} icon={FileText} tone="rose"
          sub={`${data.sops.approved} approved · ${data.sops.draft} draft`}
          testId="gd-stat-sops"
          onClick={() => setDrawer({ key: "sops", title: `SOPs — ${scopeLabel}`, subtitle: `${data.sops.total} total` })} />
        <StatCard label="Skills logged" value={counts.skills_total} icon={Star} tone="amber"
          sub={data.top_skills.length ? `Top: ${data.top_skills.slice(0, 3).map((s) => s.name).join(", ")}` : "No skills yet"}
          testId="gd-stat-skills" onClick={() => nav("/global?tab=skills")} />
      </div>

      {/* Row 3 — sign-off progress bar */}
      <div className="border border-zinc-200 bg-white p-5 rounded-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Sign-off progress (platform)</div>
            <div className="text-xs text-zinc-500">All approved policies + SOPs across every BU.</div>
          </div>
          <div className="text-xs text-zinc-500 tabular-nums" data-testid="gd-signoff-pct">
            {data.signoff.total_signed} / {data.signoff.total_required} signatures · {data.signoff.pct}%
          </div>
        </div>
        <div className="w-full h-2 bg-zinc-100 rounded-sm overflow-hidden">
          <div className="h-full bg-emerald-500" style={{ width: `${data.signoff.pct}%` }} />
        </div>
        {data.signoff.top_pending.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Top docs still awaiting signatures</div>
            <ul className="space-y-1.5">
              {data.signoff.top_pending.map((p) => (
                <li key={p.id} className="flex items-center justify-between text-sm border-t border-zinc-100 pt-1.5">
                  <Link to={`/policies/${p.id}`} className="hover:underline" data-testid={`gd-signoff-link-${p.id}`}>
                    <span className="text-[10px] uppercase mr-2 text-zinc-400">{p.doc_type}</span>{p.name}
                  </Link>
                  <span className="tabular-nums text-xs text-amber-700">{p.signed}/{p.total}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Row 4 — quarter grid (clickable) — denominator is user count, not review count */}
      <div className="border border-zinc-200 bg-white p-5 rounded-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">KPA reviews by quarter — {scopeLabel}</div>
          <div className="text-[10px] text-zinc-400">completed / users</div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {["Q1", "Q2", "Q3", "Q4"].map((q, i) => {
            const s = data.reviews_by_quarter[q];
            const pctOfUsers = usersDenom ? Math.round(100 * s.completed / usersDenom) : 0;
            const tones = ["from-sky-50 to-white border-sky-200", "from-emerald-50 to-white border-emerald-200",
                           "from-violet-50 to-white border-violet-200", "from-amber-50 to-white border-amber-200"];
            return (
              <Link key={q} to={buFilter ? `/reviews?bu=${buFilter}&quarter=${q}` : `/reviews?quarter=${q}`} data-testid={`gd-quarter-${q}`}
                className={`border rounded-sm p-3 transition-all block bg-gradient-to-b ${tones[i]} hover:shadow-md hover:-translate-y-0.5`}>
                <div className="text-xs font-semibold text-zinc-700">{q}</div>
                <div className="text-2xl font-semibold tabular-nums mt-1 text-zinc-900">
                  {s.completed}<span className="text-sm text-zinc-400">/{usersDenom}</span>
                </div>
                <div className="text-[11px] text-zinc-500">{pctOfUsers}% of users done</div>
                <div className="text-[11px] text-zinc-500">Avg score: <b>{s.avg_score ?? "—"}</b></div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Row 5 — Pending approvals + top skills */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-zinc-200 bg-white p-5 rounded-sm">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-3 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Pending approvals ({data.pending_approvals.templates.length + data.pending_approvals.policies.length})
          </div>
          {data.pending_approvals.templates.length === 0 && data.pending_approvals.policies.length === 0 ? (
            <div className="text-sm text-zinc-400">Everything is up to date.</div>
          ) : (
            <>
              {data.pending_approvals.templates.map((t) => (
                <div key={`t-${t.id}`} className="flex justify-between text-sm py-1.5 border-t border-zinc-100 first:border-0" data-testid={`gd-pending-tpl-${t.id}`}>
                  <Link to={`/templates/${t.id}/edit`} className="hover:underline flex-1 truncate">
                    <span className="text-[10px] uppercase text-zinc-400 mr-2">Template</span>{t.name}
                  </Link>
                  <span className="text-[10px] uppercase text-amber-700">{(t.status || "").replace("_", " ")}</span>
                </div>
              ))}
              {data.pending_approvals.policies.map((p) => (
                <div key={`p-${p.id}`} className="flex justify-between text-sm py-1.5 border-t border-zinc-100 first:border-0" data-testid={`gd-pending-pol-${p.id}`}>
                  <Link to={`/policies/${p.id}`} className="hover:underline flex-1 truncate">
                    <span className="text-[10px] uppercase text-zinc-400 mr-2">{p.doc_type}</span>{p.name}
                  </Link>
                  <span className="text-[10px] uppercase text-amber-700">pending</span>
                </div>
              ))}
              <div className="mt-3">
                <Link to="/global?tab=approvals" className="text-xs hover:underline" data-testid="gd-open-approvals">Open Approvals workspace →</Link>
              </div>
            </>
          )}
        </div>

        <div className="border border-zinc-200 bg-white p-5 rounded-sm">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-3 flex items-center gap-2">
            <Star className="w-3.5 h-3.5" /> Top skills across the platform
          </div>
          {data.top_skills.length === 0 ? (
            <div className="text-sm text-zinc-400">No skills logged yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {data.top_skills.map((s) => (
                <li key={s.name} className="flex items-center justify-between text-sm" data-testid={`gd-topskill-${s.name}`}>
                  <button onClick={() => nav("/global?tab=skills")} className="hover:underline text-left">{s.name}</button>
                  <span className="text-xs text-zinc-500 tabular-nums">{s.count} people · avg <b>{s.avg_rating}</b>/5</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Row 6 — per-BU breakdown table (fully clickable) */}
      <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Business Units — click a row to open</div>
          <Link to="/business-units" className="text-xs hover:underline" data-testid="gd-open-bugrid">Open BU grid →</Link>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Business Unit</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Users</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Profiles</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Templates</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Reviews</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Policies / SOPs</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Pending</th>
              </tr>
            </thead>
            <tbody>
              {data.per_business_unit.map((r) => (
                <tr key={r.id} onClick={() => nav(`/business-units/${r.id}`)}
                    className="cursor-pointer hover:bg-zinc-50 border-t border-zinc-100"
                    data-testid={`gd-burow-${r.id}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-3">
                      {r.logo_url ? (
                        <div className="w-16 h-9 flex items-center justify-center bg-white border border-zinc-100 rounded-sm px-1 shrink-0">
                          <img src={r.logo_url} alt="" className="max-h-8 max-w-full object-contain" />
                        </div>
                      ) : (
                        <div className="w-16 h-9 rounded-sm bg-gradient-to-br from-sky-100 to-violet-100 flex items-center justify-center text-xs font-bold text-sky-700 shrink-0">
                          {(r.name || "?").slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <span className="font-medium">{r.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.users}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.position_profiles}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.templates}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    <span className="text-emerald-700">{r.reviews_completed}</span>
                    <span className="text-zinc-400">/{r.reviews_total}</span>
                    <span className="text-[11px] text-zinc-500 ml-1">({r.reviews_pct}%)</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.policies_total} / {r.sops_total}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.policies_pending > 0
                      ? <span className="inline-flex items-center gap-1 text-amber-700 text-xs"><AlertTriangle className="w-3 h-3" />{r.policies_pending}</span>
                      : <span className="text-zinc-300">—</span>}
                  </td>
                </tr>
              ))}
              {data.per_business_unit.length === 0 && (
                <tr><td colSpan={7} className="text-center text-sm text-zinc-400 py-8">No Business Units yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Row 7 — Recent activity (click through) */}
      <div className="border border-zinc-200 bg-white p-5 rounded-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-2">
            <ActivityIcon className="w-3.5 h-3.5" /> Recent activity (system-wide)
          </div>
          <Link to="/global?tab=audit" className="text-xs hover:underline" data-testid="gd-open-audit">Open Audit Log →</Link>
        </div>
        {data.recent_activity.length === 0 ? (
          <div className="text-sm text-zinc-400">Nothing yet.</div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {data.recent_activity.map((e) => (
              <li key={e.id} className="py-2 text-sm flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="truncate">{e.summary || e.action}</div>
                  <div className="text-[11px] text-zinc-500">{e.actor_email} · {e.action}</div>
                </div>
                <div className="text-[11px] text-zinc-400 whitespace-nowrap">{(e.created_at || "").split("T")[0]}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {drawer && (
        <DrillDownDrawer drawer={drawer} onClose={() => setDrawer(null)} data={data} />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Drill-down drawer — hits a companion endpoint or filters state.
// ─────────────────────────────────────────────────────────────
function DrillDownDrawer({ drawer, onClose, data }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  useEffect(() => {
    (async () => {
      try {
        if (drawer.key === "users") {
          const { data } = await api.get("/users");
          setRows(data);
        } else if (drawer.key === "profiles") {
          const { data } = await api.get("/departments");
          setRows(data);
        } else if (drawer.key === "templates") {
          const { data } = await api.get("/templates");
          setRows(data);
        } else if (drawer.key === "policies" || drawer.key === "sops") {
          const { data } = await api.get("/policies");
          setRows(data.filter((p) => (p.doc_type || "policy") === (drawer.key === "sops" ? "sop" : "policy")));
        }
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, [drawer.key]);

  return (
    <ListDrawer title={drawer.title} subtitle={drawer.subtitle} onClose={onClose} testId={`drawer-${drawer.key}`}>
      {err && <div className="text-sm text-red-700">{err}</div>}
      {!rows && !err && <div className="text-sm text-zinc-500">Loading…</div>}
      {rows && rows.length === 0 && <div className="text-sm text-zinc-400">Nothing to show.</div>}
      {rows && rows.length > 0 && (
        <ul className="divide-y divide-zinc-100">
          {drawer.key === "users" && rows.map((u) => (
            <li key={u.id} className="py-2 flex items-center justify-between gap-3" data-testid={`drawer-user-${u.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{u.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{u.email} · <span className="uppercase">{u.role}</span></div>
              </div>
              {u.business_unit_id && (
                <button onClick={() => { onClose(); nav(`/business-units/${u.business_unit_id}`); }}
                  className="text-xs px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50">Open BU</button>
              )}
            </li>
          ))}
          {drawer.key === "profiles" && rows.map((d) => (
            <li key={d.id} className="py-2 flex items-center justify-between gap-3" data-testid={`drawer-profile-${d.id}`}>
              <span className="text-sm">{d.name}</span>
              <button onClick={() => { onClose(); nav(`/business-units/${d.business_unit_id}`); }}
                className="text-xs px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50">Open BU</button>
            </li>
          ))}
          {drawer.key === "templates" && rows.map((t) => (
            <li key={t.id} className="py-2 flex items-center justify-between gap-3" data-testid={`drawer-tpl-${t.id}`}>
              <Link to={`/templates/${t.id}/view`} onClick={onClose} className="text-sm hover:underline flex-1 truncate">{t.name}</Link>
              <span className="text-[10px] uppercase text-zinc-500">{(t.status || "").replace("_", " ")}</span>
            </li>
          ))}
          {(drawer.key === "policies" || drawer.key === "sops") && rows.map((p) => (
            <li key={p.id} className="py-2 flex items-center justify-between gap-3" data-testid={`drawer-pol-${p.id}`}>
              <Link to={`/policies/${p.id}`} onClick={onClose} className="text-sm hover:underline flex-1 truncate">{p.name}</Link>
              <span className="text-[10px] uppercase text-zinc-500">{p.status || "approved"}</span>
            </li>
          ))}
        </ul>
      )}
    </ListDrawer>
  );
}


// ─────────────────────────────────────────────────────────────
// Global Skills Matrix — platform-wide table with BU column & filter.
// ─────────────────────────────────────────────────────────────
export function GlobalSkillsMatrix() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [buFilter, setBuFilter] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/global/skills-matrix${buFilter ? `?bu_id=${buFilter}` : ""}`);
        setData(data);
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, [buFilter]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data.rows;
    const ql = q.trim().toLowerCase();
    return data.rows.filter((r) =>
      (r.name || "").toLowerCase().includes(ql) ||
      (r.email || "").toLowerCase().includes(ql) ||
      (r.business_unit_name || "").toLowerCase().includes(ql)
    );
  }, [data, q]);

  if (err) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3">{err}</div>;
  if (!data) return <div className="text-sm text-zinc-500">Loading platform skills matrix…</div>;

  return (
    <div data-testid="global-skills-matrix">
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Platform skills matrix</div>
        <div className="text-xs text-zinc-500 mt-1">
          Every skill logged by every employee across every Business Unit. Ratings 1 (novice) → 5 (expert).
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <select value={buFilter} onChange={(e) => setBuFilter(e.target.value)}
          data-testid="gsm-bu-filter"
          className="border border-zinc-300 rounded-sm px-3 py-1.5 text-sm bg-white">
          <option value="">All Business Units</option>
          {data.business_units.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter people or BU…"
          data-testid="gsm-search" className="border border-zinc-300 rounded-sm px-3 py-1.5 text-sm bg-white flex-1 max-w-xs" />
        <div className="text-xs text-zinc-500 tabular-nums ml-auto">
          {filteredRows.length} people · {data.skills.length} distinct skills
        </div>
      </div>

      {filteredRows.length === 0 ? (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-10 text-center">
          No skills logged yet{buFilter ? " for this BU" : ""}.
        </div>
      ) : (
        <div className="overflow-auto border border-zinc-200 rounded-sm max-h-[70vh]">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 border-b border-zinc-200 sticky left-0 bg-zinc-50 z-20 min-w-[220px]">Employee</th>
                <th className="text-left px-3 py-2 border-b border-zinc-200 sticky left-[220px] bg-zinc-50 z-20 min-w-[160px]">Business Unit</th>
                {data.skills.map((s) => (
                  <th key={s} className="text-center px-3 py-2 border-b border-zinc-200 text-[11px] font-medium whitespace-nowrap">{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.user_id} className="odd:bg-white even:bg-zinc-50/50">
                  <td className="px-3 py-2 border-b border-zinc-100 sticky left-0 bg-inherit">
                    <div className="text-sm font-medium">{r.name}</div>
                    <div className="text-[11px] text-zinc-500">{r.title || "—"}</div>
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 sticky left-[220px] bg-inherit">
                    {r.business_unit_id ? (
                      <Link to={`/business-units/${r.business_unit_id}`} className="text-[12px] hover:underline">{r.business_unit_name}</Link>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                  {data.skills.map((s) => {
                    const rating = r.ratings?.[s.toLowerCase()];
                    return (
                      <td key={s} className="px-3 py-2 border-b border-zinc-100 text-center">
                        {rating ? (
                          <div className="inline-flex items-center gap-0.5" data-testid={`gsm-cell-${r.user_id}-${s}`}>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <Star key={n} className={`w-3 h-3 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-200"}`} />
                            ))}
                          </div>
                        ) : (<span className="text-zinc-300">—</span>)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
